import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select from "react-select";
import ExportBar from "../../Components/Accounting/ExportBar";
import { ajaxTax } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString("en-GB", { month: "long" }),
}));

const TAB_CONFIG = [
  { key: "paye",  label: "PAYE Monthly",      icon: "fa-percent" },
  { key: "p10",   label: "P10 Annual",         icon: "fa-file-lines" },
  { key: "vat",   label: "VAT Return",         icon: "fa-receipt" },
  { key: "nssf",  label: "NSSF Schedule",      icon: "fa-shield-halved" },
  { key: "wht",   label: "Withholding Tax",    icon: "fa-hand-holding-dollar" },
];

const TaxFilings = () => {
  const [tab,     setTab]     = useState("paye");
  const [month,   setMonth]   = useState(MONTHS[new Date().getMonth()]);
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setData(null);
    const params = { period_month: month.value, period_year: year };
    let res;
    switch (tab) {
      case "paye":  res = await ajaxTax.getPayeMonthly(params); break;
      case "p10":   res = await ajaxTax.getPayeAnnual({ period_year: year }); break;
      case "vat":   res = await ajaxTax.getVatReturn(params);   break;
      case "nssf":  res = await ajaxTax.getNssfSchedule(params); break;
      case "wht":   res = await ajaxTax.getWhtReturn(params);   break;
      default:      res = await ajaxTax.getPayeMonthly(params);
    }
    if (res.status === "OK") setData(res.details);
    else toast.error(res.message || "Failed to generate report");
    setLoading(false);
  };

  // ── PAYE TABLE ────────────────────────────────────────────────────────────
  const renderPAYE = () => {
    const rows = data?.payslips || data?.employees || [];
    return (
      <div>
        <div className="row g-2 mb-3">
          {[
            { label: "Total Gross Pay",   value: data?.total_gross,   color: "#10b981" },
            { label: "Total PAYE",        value: data?.total_paye,    color: "#ef4444" },
            { label: "NSSF (Employee)",   value: data?.total_nssf_ee, color: "#f59e0b" },
            { label: "NSSF (Employer)",   value: data?.total_nssf_er, color: "#6366f1" },
            { label: "LST",               value: data?.total_lst,     color: "#8b5cf6" },
            { label: "Total Net Pay",     value: data?.total_net,     color: "#0ea5e9" },
          ].map((c, i) => (
            <div key={i} className="col-xl-2 col-sm-4">
              <div className="card p-2" style={{ borderLeft: `3px solid ${c.color}` }}>
                <div className="tx-11 tx-muted">{c.label}</div>
                <div className="fw-bold tx-13" style={{ color: c.color }}>{functions.formatCurrency(c.value)}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead className="bg-light">
              <tr><th>Employee</th><th>TIN</th><th>Basic</th><th>Gross</th><th>PAYE</th><th>NSSF (Ee)</th><th>NSSF (Er)</th><th>LST</th><th>Net</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><strong>{r.full_name}</strong><br/><small className="tx-muted">{r.employee_number}</small></td>
                  <td className="tx-12">{r.tin || "—"}</td>
                  <td>{functions.formatCurrency(r.basic_salary)}</td>
                  <td>{functions.formatCurrency(r.gross_pay)}</td>
                  <td className="tx-danger">{functions.formatCurrency(r.paye)}</td>
                  <td className="tx-danger">{functions.formatCurrency(r.employee_nssf)}</td>
                  <td className="tx-danger">{functions.formatCurrency(r.employer_nssf)}</td>
                  <td className="tx-danger">{functions.formatCurrency(r.lst)}</td>
                  <td className="tx-success fw-bold">{functions.formatCurrency(r.net_pay)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="fw-bold bg-light">
              <tr>
                <td colSpan={3}>Totals</td>
                <td>{functions.formatCurrency(data?.total_gross)}</td>
                <td>{functions.formatCurrency(data?.total_paye)}</td>
                <td>{functions.formatCurrency(data?.total_nssf_ee)}</td>
                <td>{functions.formatCurrency(data?.total_nssf_er)}</td>
                <td>{functions.formatCurrency(data?.total_lst)}</td>
                <td className="tx-success">{functions.formatCurrency(data?.total_net)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <ExportBar data={rows}
          columns={[
            { key:"full_name",label:"Name" }, { key:"tin",label:"TIN" },
            { key:"gross_pay",label:"Gross" }, { key:"paye",label:"PAYE" },
            { key:"employee_nssf",label:"NSSF(Ee)" }, { key:"employer_nssf",label:"NSSF(Er)" },
            { key:"lst",label:"LST" }, { key:"net_pay",label:"Net Pay" },
          ]}
          filename={`paye-${month.label}-${year}`}
          pdfTitle={`PAYE Return — ${month.label} ${year}`}
          printCode="ACC-TAX" exportCode="ACC-TAX" className="mt-3" />
      </div>
    );
  };

  // ── NSSF TABLE ────────────────────────────────────────────────────────────
  const renderNSSF = () => {
    const rows = data?.employees || [];
    return (
      <div>
        <div className="alert alert-info tx-13 mb-3">
          NSSF Contribution Rate: Employee <strong>5%</strong> | Employer <strong>10%</strong>
          &nbsp;|&nbsp; Total: <strong>{functions.formatCurrency(data?.total_nssf)}</strong>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead className="bg-light"><tr><th>Name</th><th>NSSF No.</th><th>Gross</th><th>Employee (5%)</th><th>Employer (10%)</th><th>Total</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><strong>{r.full_name}</strong></td>
                  <td className="tx-12">{r.nssf_number || "—"}</td>
                  <td>{functions.formatCurrency(r.gross_pay)}</td>
                  <td>{functions.formatCurrency(r.employee_nssf)}</td>
                  <td>{functions.formatCurrency(r.employer_nssf)}</td>
                  <td className="fw-bold">{functions.formatCurrency((r.employee_nssf || 0) + (r.employer_nssf || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ExportBar data={rows}
          columns={[{ key:"full_name",label:"Name" }, { key:"nssf_number",label:"NSSF No." }, { key:"gross_pay",label:"Gross" }, { key:"employee_nssf",label:"Employee" }, { key:"employer_nssf",label:"Employer" }]}
          filename={`nssf-${month.label}-${year}`} pdfTitle={`NSSF Schedule — ${month.label} ${year}`}
          printCode="ACC-TAX" exportCode="ACC-TAX" className="mt-3" />
      </div>
    );
  };

  // ── VAT TABLE ─────────────────────────────────────────────────────────────
  const renderVAT = () => (
    <div>
      <div className="row g-2 mb-3">
        {[
          { label: "Output VAT (Sales)",  value: data?.output_vat,   color: "#ef4444" },
          { label: "Input VAT (Purchases)", value: data?.input_vat, color: "#10b981" },
          { label: "Net VAT Payable",     value: data?.net_vat,      color: "#4f46e5" },
        ].map((c, i) => (
          <div key={i} className="col-lg-4">
            <div className="card p-3" style={{ borderLeft: `3px solid ${c.color}` }}>
              <div className="tx-12 tx-muted">{c.label}</div>
              <div className="fw-bold tx-18" style={{ color: c.color }}>{functions.formatCurrency(c.value)}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="alert alert-info tx-13">
        VAT Rate: <strong>18%</strong>. Net VAT is Output minus Input. A positive value means tax is due to URA.
      </div>
      {data?.transactions && (
        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead className="bg-light"><tr><th>Date</th><th>Description</th><th>Type</th><th>Amount</th><th>VAT</th></tr></thead>
            <tbody>
              {data.transactions.map((t, i) => (
                <tr key={i}>
                  <td>{functions.formatDate(t.date)}</td>
                  <td className="tx-12">{t.description}</td>
                  <td><span className={`badge ${t.type === "output" ? "bg-danger" : "bg-success"}`}>{t.type}</span></td>
                  <td>{functions.formatCurrency(t.amount)}</td>
                  <td className="fw-bold">{functions.formatCurrency(t.vat_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (loading) return <div className="text-center py-5"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i><p className="mt-2 tx-muted">Generating report...</p></div>;
    if (!data)   return <div className="text-center py-5 tx-muted"><i className="fa-solid fa-chart-bar" style={{ fontSize:"2.5rem",opacity:0.3 }}></i><p className="mt-2">Click "Generate" to view the report.</p></div>;
    switch (tab) {
      case "paye":  return renderPAYE();
      case "nssf":  return renderNSSF();
      case "vat":   return renderVAT();
      default:      return <pre className="tx-12" style={{ fontSize:"0.8rem",overflow:"auto" }}>{JSON.stringify(data, null, 2)}</pre>;
    }
  };

  return (
    <AppContainer title="Tax Filings" previousPageTitle="Accounting"
      description="Uganda statutory tax reports: PAYE, NSSF schedules, VAT returns, and Withholding Tax. Generate, review, and export for URA filing."
      actions={[{ label: "Generate Report", icon: "fa-solid fa-play", className: "btn btn-primary", onClick: fetchReport }]}>
      <Toaster />

      {/* Controls */}
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">Month</label>
            <Select options={MONTHS} value={month} onChange={setMonth} />
          </div>
          <div className="col-lg-2">
            <label className="form-label tx-12 fw-semibold">Year</label>
            <input type="number" className="form-control" value={year} min="2020" max="2099" onChange={(e) => setYear(parseInt(e.target.value))} />
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <ul className="nav nav-tabs mb-3">
        {TAB_CONFIG.map((t) => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => { setTab(t.key); setData(null); }}>
              <i className={`fa-solid ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="card custom-card p-4">{renderContent()}</div>
    </AppContainer>
  );
};

export default TaxFilings;
